import React, { useState, useCallback, useRef } from 'react';
import api from '../services/api';
import s from './DRE.module.css';
import InputDataFiltro from '../components/common/InputDataFiltro';

const fmtMoeda = v => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtData  = str => str ? new Date(String(str).slice(0, 10) + 'T12:00:00').toLocaleDateString('pt-BR') : '—';

const TIPO_SINAL = { receita: '+', custo: '-', despesa: '-', outro: '±' };
const TIPO_COR   = { receita: '#15803D', custo: '#92400E', despesa: '#B91C1C', outro: '#64748B' };

// Meses para presets
const MESES = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];

function mesAtual() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  return { de: `${y}-${m}-01`, ate: `${y}-${m}-${ultimoDia(y, d.getMonth() + 1)}` };
}
function anoAtual() {
  const y = new Date().getFullYear();
  return { de: `${y}-01-01`, ate: `${y}-12-31` };
}
function ultimoDia(ano, mes) {
  return new Date(ano, mes, 0).getDate();
}

export default function DRE() {
  const hoje = new Date();
  const [periodo, setPeriodo] = useState(mesAtual());
  const [dre, setDre]         = useState(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro]       = useState('');
  const [collapsed, setCol]   = useState(new Set());
  const printRef              = useRef();

  const carregar = useCallback(async (p = periodo) => {
    setLoading(true); setErro('');
    try {
      const { data } = await api.get('/financeiro/dre', { params: p });
      setDre(data);
      // Colapsar nivel 3 por padrão para melhor visão geral
      const col = new Set(data.contas.filter(c => c.nivel >= 3).map(c => c.id));
      setCol(col);
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao gerar DRE.');
    } finally { setLoading(false); }
  }, [periodo]);

  const setPre = (p) => { setPeriodo(p); carregar(p); };

  const setMes = (m) => {
    const y = hoje.getFullYear();
    const mes = m + 1;
    const p = { de: `${y}-${String(mes).padStart(2,'0')}-01`, ate: `${y}-${String(mes).padStart(2,'0')}-${ultimoDia(y, mes)}` };
    setPre(p);
  };

  const toggleCol = (id) => {
    setCol(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  // Resumido = colapsa toda conta sintética (com filhos), sobrando só os
  // totais por grupo. Detalhado = expande tudo. Reaproveita o mecanismo de
  // colapso por linha já existente — inclusive dá para abrir um ramo
  // específico mesmo em modo resumido. Imprimir usa a visão atual da tela.
  const idsComFilhos = dre
    ? new Set(dre.contas.filter(c => dre.contas.some(f => f.pai_id === c.id)).map(c => c.id))
    : new Set();
  const modoResumido = idsComFilhos.size > 0 && [...idsComFilhos].every(id => collapsed.has(id));
  const verResumido  = () => setCol(new Set(idsComFilhos));
  const verDetalhado = () => setCol(new Set());

  const imprimir = () => window.print();

  // Verificar se conta está visível (ancestrais não colapsados)
  function isVisible(conta, idMap) {
    let cur = conta;
    while (cur.pai_id) {
      if (collapsed.has(cur.pai_id)) return false;
      cur = idMap[cur.pai_id];
      if (!cur) break;
    }
    return true;
  }

  const idMap = dre ? Object.fromEntries(dre.contas.map(c => [c.id, c])) : {};
  const temFilhos = id => dre ? dre.contas.some(c => c.pai_id === id) : false;

  const sum = dre?.sumario || {};

  return (
    <div className={s.page}>
      {/* Cabeçalho */}
      <div className={s.header}>
        <div>
          <h1 className={s.titulo}>DRE — Demonstração do Resultado</h1>
          <p className={s.sub}>Resultado econômico por categoria do Plano de Contas</p>
        </div>
        <div className={s.headerAcoes}>
          {dre && (
            <>
              <button className={modoResumido ? s.btnPri : s.btnSec} onClick={verResumido}
                title="Mostra só os totais por grupo (contas sintéticas), sem os lançamentos analíticos">
                📋 Resumido
              </button>
              <button className={!modoResumido ? s.btnPri : s.btnSec} onClick={verDetalhado}
                title="Mostra todos os níveis de conta, do grupo até o lançamento analítico">
                📑 Detalhado
              </button>
            </>
          )}
          <button className={s.btnSec} onClick={imprimir}
            title="Imprime a visão atual da tela (resumido ou detalhado)">
            🖨 Imprimir
          </button>
        </div>
      </div>

      {/* Filtro de período */}
      <div className={s.filtroBar}>
        <div className={s.filtroGrupo}>
          <label>De</label>
          <InputDataFiltro className={s.inputData} value={periodo.de}
            onChange={e => setPeriodo(p => ({...p, de: e.target.value}))} />
          <label>Até</label>
          <InputDataFiltro className={s.inputData} value={periodo.ate}
            onChange={e => setPeriodo(p => ({...p, ate: e.target.value}))} />
          <button className={s.btnPri} onClick={() => carregar()}>Gerar DRE</button>
        </div>
        <div className={s.filtroGrupo}>
          <span className={s.preLabel}>Atalhos:</span>
          <button className={s.btnPre} onClick={() => setPre(mesAtual())}>Mês atual</button>
          <button className={s.btnPre} onClick={() => setPre(anoAtual())}>Ano {hoje.getFullYear()}</button>
          {MESES.map((m, i) => (
            <button key={i} className={s.btnPre} onClick={() => setMes(i)}>{m}</button>
          ))}
        </div>
      </div>

      {erro && <div className={s.alerta}>{erro}</div>}
      {loading && <div className={s.carregando}>Gerando demonstração...</div>}

      {dre && !loading && (
        <div ref={printRef}>
          {/* Cabeçalho do relatório */}
          <div className={s.relHeader}>
            <div className={s.relTitulo}>DEMONSTRAÇÃO DO RESULTADO DO EXERCÍCIO</div>
            <div className={s.relPeriodo}>
              Período: {fmtData(dre.periodo.de)} a {fmtData(dre.periodo.ate)}
            </div>
          </div>

          {/* Cards de sumário */}
          <div className={s.cards}>
            <Card titulo="Receita Total"     valor={sum.total_receita}         cor="#15803D"
              explicacao="Soma de todas as contas do tipo Receita categorizadas no período (ex.: venda de mercadorias)." />
            <Card titulo="Custos"            valor={-sum.total_custo}          cor="#92400E"
              explicacao="Soma de todas as contas do tipo Custo categorizadas no período (ex.: CMV — custo da mercadoria vendida)." />
            <Card titulo="Lucro Bruto"       valor={sum.lucro_bruto}           cor={sum.lucro_bruto >= 0 ? '#1565FF' : '#B91C1C'} destaque indicaPrejuizo
              explicacao="Lucro Bruto = Receita Total − Custos" />
            <Card titulo="Despesas"          valor={-sum.total_despesa}        cor="#B91C1C"
              explicacao="Soma de todas as contas do tipo Despesa categorizadas no período (despesas operacionais: aluguel, salários, taxas etc.)." />
            <Card titulo="Resultado"         valor={sum.resultado_liquido}     cor={sum.resultado_liquido >= 0 ? '#15803D' : '#B91C1C'} destaque indicaPrejuizo
              explicacao="Resultado Líquido = Lucro Bruto − Despesas ± Outras Receitas/Despesas" />
          </div>

          {/* Aviso se sem dados */}
          {dre.contas.every(c => !c.total) && (
            <div className={s.avisoVazio}>
              Nenhum lançamento categorizado no período.
              Para aparecer no DRE, selecione a <strong>Categoria (Plano de Contas)</strong> ao lançar em Contas a Pagar ou a Receber.
            </div>
          )}

          {/* Corpo do DRE */}
          <div className={s.dreWrap}>
            <table className={s.dreTable}>
              <thead>
                <tr>
                  <th className={s.thCod}>Código</th>
                  <th className={s.thDesc}>Descrição</th>
                  <th className={s.thValor}>Valor (R$)</th>
                  <th className={s.thAV}>AV%</th>
                </tr>
              </thead>
              <tbody>
                {/* RECEITAS */}
                <SeparadorDRE label="(+) RECEITAS" />
                {dre.contas
                  .filter(c => c.tipo === 'receita' && isVisible(c, idMap))
                  .map(c => (
                    <LinhaContaDRE key={c.id} conta={c}
                      collapsed={collapsed.has(c.id)} temFilhos={temFilhos(c.id)}
                      onToggle={() => toggleCol(c.id)}
                      totalReceita={sum.total_receita}
                      sinal="+" />
                  ))
                }
                <TotalDRE label="TOTAL RECEITAS" valor={sum.total_receita} cor="#15803D" />

                {/* CUSTOS */}
                <SeparadorDRE label="(-) CUSTOS" />
                {dre.contas
                  .filter(c => c.tipo === 'custo' && isVisible(c, idMap))
                  .map(c => (
                    <LinhaContaDRE key={c.id} conta={c}
                      collapsed={collapsed.has(c.id)} temFilhos={temFilhos(c.id)}
                      onToggle={() => toggleCol(c.id)}
                      totalReceita={sum.total_receita}
                      sinal="-" />
                  ))
                }
                <TotalDRE label="TOTAL CUSTOS" valor={-sum.total_custo} cor="#92400E" />

                {/* LUCRO BRUTO */}
                <ResultadoParcial label="(=) LUCRO BRUTO" valor={sum.lucro_bruto} />

                {/* DESPESAS */}
                <SeparadorDRE label="(-) DESPESAS OPERACIONAIS" />
                {dre.contas
                  .filter(c => c.tipo === 'despesa' && isVisible(c, idMap))
                  .map(c => (
                    <LinhaContaDRE key={c.id} conta={c}
                      collapsed={collapsed.has(c.id)} temFilhos={temFilhos(c.id)}
                      onToggle={() => toggleCol(c.id)}
                      totalReceita={sum.total_receita}
                      sinal="-" />
                  ))
                }
                <TotalDRE label="TOTAL DESPESAS" valor={-sum.total_despesa} cor="#B91C1C" />

                {/* RESULTADO OPERACIONAL */}
                <ResultadoParcial label="(=) RESULTADO OPERACIONAL" valor={sum.resultado_operacional} />

                {/* OUTRAS RECEITAS/DESPESAS */}
                {dre.contas.some(c => c.tipo === 'outro') && (
                  <>
                    <SeparadorDRE label="(±) OUTRAS RECEITAS / DESPESAS" />
                    {dre.contas
                      .filter(c => c.tipo === 'outro' && isVisible(c, idMap))
                      .map(c => (
                        <LinhaContaDRE key={c.id} conta={c}
                          collapsed={collapsed.has(c.id)} temFilhos={temFilhos(c.id)}
                          onToggle={() => toggleCol(c.id)}
                          totalReceita={sum.total_receita}
                          sinal="±" />
                      ))
                    }
                    <TotalDRE label="TOTAL OUTROS" valor={sum.outras_receitas_desp} cor="#64748B" />
                  </>
                )}

                {/* RESULTADO LÍQUIDO */}
                <tr>
                  <td colSpan={4} style={{ padding: 0 }}>
                    <div className={`${s.resultadoFinal} ${sum.resultado_liquido >= 0 ? s.positivo : s.negativo}`}>
                      <span className={s.rfLabel}>(=) RESULTADO LÍQUIDO DO PERÍODO</span>
                      <span className={s.rfValor}>{fmtMoeda(sum.resultado_liquido)}</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!dre && !loading && (
        <div className={s.inicio}>
          <div className={s.inicioIcon}>📊</div>
          <p>Selecione o período e clique em <strong>Gerar DRE</strong></p>
          <p className={s.inicioSub}>O relatório consolida os lançamentos categorizados no Plano de Contas.</p>
          <button className={s.btnPri} onClick={() => carregar()}>Gerar DRE do mês atual</button>
        </div>
      )}
    </div>
  );
}

function Card({ titulo, valor, cor, destaque, indicaPrejuizo, explicacao }) {
  const positivo = valor >= 0;
  return (
    <div className={`${s.card} ${destaque ? s.cardDestaque : ''}`} title={explicacao}>
      <div className={s.cardTitulo}>{titulo}{explicacao ? ' ⓘ' : ''}</div>
      <div className={s.cardValor} style={{ color: cor || (positivo ? '#15803D' : '#B91C1C') }}>
        {fmtMoeda(Math.abs(valor))}
      </div>
      {indicaPrejuizo && valor < 0 && <div className={s.cardNeg}>prejuízo</div>}
    </div>
  );
}

function SeparadorDRE({ label }) {
  return (
    <tr className={s.separador}>
      <td colSpan={4}><span className={s.sepLabel}>{label}</span></td>
    </tr>
  );
}

function TotalDRE({ label, valor, cor }) {
  return (
    <tr className={s.totalRow}>
      <td colSpan={2} className={s.totalLabel}>{label}</td>
      <td className={s.totalValor} style={{ color: cor }}>{fmtMoeda(Math.abs(valor))}</td>
      <td></td>
    </tr>
  );
}

function ResultadoParcial({ label, valor }) {
  const pos = valor >= 0;
  return (
    <tr className={`${s.resultadoParcial} ${pos ? s.rpPositivo : s.rpNegativo}`}>
      <td colSpan={2} className={s.rpLabel}>{label}</td>
      <td className={s.rpValor}>{fmtMoeda(valor)}</td>
      <td></td>
    </tr>
  );
}

function LinhaContaDRE({ conta, collapsed, temFilhos, onToggle, totalReceita, sinal }) {
  const isSin   = conta.natureza === 'sintetica';
  const indent  = (conta.nivel - 1) * 18;
  const av       = totalReceita > 0 ? ((conta.total / totalReceita) * 100).toFixed(1) : '—';
  const temVal   = Math.abs(Number(conta.total) || 0) > 0.004;
  const negativo = Number(conta.total) < 0;

  return (
    <tr className={`${s.contaLinha} ${isSin ? s.contaSin : s.contaAn} ${!temVal ? s.semValor : ''}`}>
      <td className={s.tdCod}>
        <span className={s.cod}>{conta.codigo}</span>
      </td>
      <td className={s.tdDesc}>
        <div style={{ display: 'flex', alignItems: 'center', paddingLeft: indent }}>
          {temFilhos ? (
            <button className={s.toggle} onClick={onToggle}>{collapsed ? '▶' : '▼'}</button>
          ) : (
            <span className={s.toggleSp} />
          )}
          <span className={isSin ? s.descSin : s.descAn}>{conta.descricao}</span>
        </div>
      </td>
      <td className={s.tdValor}>
        {temVal ? (
          <span style={{ color: (sinal === '+' && !negativo) ? '#15803D' : '#B91C1C', fontWeight: isSin ? 700 : 400 }}>
            {(sinal !== '+' || negativo) ? '(' : ''}{fmtMoeda(Math.abs(conta.total))}{(sinal !== '+' || negativo) ? ')' : ''}
          </span>
        ) : (
          <span className={s.semValorTxt}>—</span>
        )}
      </td>
      <td className={s.tdAV}>
        {temVal && totalReceita > 0 ? (
          <span className={s.avPct}>{av}%</span>
        ) : ''}
      </td>
    </tr>
  );
}
